import Decimal from 'decimal.js'
import { BaixaDePagamentoAuditada } from '@/models/financeiro/BaixaDePagamento'

/**
 * Juros, multa e saldo de um título a receber numa data.
 *
 * É a MESMA conta do MotorDeEncargosDoTitulo do backend, que é quem grava o título no lote
 * diário e depois de cada baixa. Mexeu num, mexa no outro: os números das telas de baixa
 * precisam bater com o que o sistema grava, senão sobra resíduo (ou falta dinheiro) no título.
 *
 * A REGRA (ticket 42385). O pagamento abate primeiro a multa e os juros já devidos, e só depois
 * o valor do título. Os encargos que ainda vão correr incidem só sobre o que falta pagar.
 *
 *  1. Baixa feita até o último dia da carência abate o principal sem encargo nenhum.
 *  2. Passada a carência, a multa nasce uma vez, sobre o principal que sobrou (crediário e
 *     boleto). Principal todo pago na carência encerra o título.
 *  3. Os juros correm desde o vencimento, período a período, sobre o principal em aberto (mais
 *     a multa em aberto, se a loja calcula juros sobre a multa; mais os juros em aberto, no
 *     composto). Cada baixa encerra um período.
 *  4. O dinheiro de cada baixa paga multa, depois juros, depois principal, qualquer que seja a
 *     divisão gravada nela. Os descontos abatem o que dizem abater.
 */

const D = Decimal.clone({ precision: 34, rounding: Decimal.ROUND_HALF_EVEN })

const TETO_DE_DIAS_DE_ATRASO = 1826
const UM_DIA_EM_MS = 24 * 60 * 60 * 1000

export interface TituloParaEncargos {
	valor: number
	/** 'YYYY-MM-DD' */
	dataDeVencimento: string
	diasDeCarencia?: number | null
	porcentagemDeJuros?: number | null
	porcentagemDeMulta?: number | null
	jurosComposto?: boolean | null
	formaDePagamento?: string | null
}

export interface BaixaParaEncargos {
	/** 'YYYY-MM-DD' */
	data: string
	principal: number
	descontoNoPrincipal: number
	juros: number
	descontoNosJuros: number
	multa: number
	descontoNaMulta: number
}

export interface EncargosApurados {
	principal: number
	juros: number
	multa: number
	valorAtualizado: number
}

export function baixasParaEncargos(baixas: BaixaDePagamentoAuditada[] | null | undefined): BaixaParaEncargos[] {
	const validas = (baixas || []).filter(baixa => !baixa.isTaxa && !!baixa.dataHoraPagamento)
	const dataPorId = new Map(validas.map(baixa => [baixa.id, String(baixa.dataHoraPagamento).substring(0, 10)]))

	return validas
		.map(baixa => ({
			data: (baixa.baixaDePagamentoOrigemId && dataPorId.get(baixa.baixaDePagamentoOrigemId))
				|| String(baixa.dataHoraPagamento).substring(0, 10),
			principal: Number(baixa.valorPagamento || 0),
			descontoNoPrincipal: Number(baixa.valorDesconto || 0),
			juros: Number(baixa.valorJurosSobreBaixa || 0),
			descontoNosJuros: Number(baixa.valorDeDescontoNosJuros || 0),
			multa: Number(baixa.valorDaMultaSobreBaixa || 0),
			descontoNaMulta: Number(baixa.valorDeDescontoNaMulta || 0),
		}))
}

/** Último dia sem encargo. Mesma regra do CalculadorDeJuros.calcularDataAposCarencia. */
export function dataAposCarencia(dataDeVencimento: string, diasDeCarencia: number): string {
	const vencimento = paraData(dataDeVencimento)
	if (diasDeCarencia > 0) return paraTexto(somarDias(vencimento, diasDeCarencia))

	const diaDaSemana = vencimento.getUTCDay()
	if (diaDaSemana === 6) return paraTexto(somarDias(vencimento, 2))
	if (diaDaSemana === 0) return paraTexto(somarDias(vencimento, 1))
	return dataDeVencimento
}

/**
 * Apura o título na data de referência. Devolve null quando o título não tem encargo a apurar
 * (juros simples fora de crediário e boleto), caso em que vale o saldo gravado.
 */
export function apurarEncargosDoTitulo(
	titulo: TituloParaEncargos,
	baixas: BaixaParaEncargos[],
	dataDeReferencia: string,
	calcularJurosComMulta: boolean,
): EncargosApurados | null {
	const composto = !!titulo.jurosComposto
	const formaGeraEncargo = titulo.formaDePagamento === 'CREDIARIO' || titulo.formaDePagamento === 'BOLETO'
	if (!composto && !formaGeraEncargo) return null

	const fimDaCarencia = dataAposCarencia(titulo.dataDeVencimento, titulo.diasDeCarencia || 0)
	const emOrdem = [...baixas].sort((a, b) => a.data.localeCompare(b.data)
		|| Number(recebido(a).lt(0)) - Number(recebido(b).lt(0)))

	// 1. Baixas da carência: abatem o principal, sem encargo.
	let principal = new D(titulo.valor || 0)
	let proxima = 0
	while (proxima < emOrdem.length && emOrdem[proxima].data <= fimDaCarencia) {
		const baixa = emOrdem[proxima++]
		principal = principal.minus(recebido(baixa)).minus(baixa.descontoNoPrincipal)
	}

	if (dataDeReferencia <= fimDaCarencia || principal.lte(0)) {
		for (; proxima < emOrdem.length; proxima++) {
			const baixa = emOrdem[proxima]
			principal = principal.minus(recebido(baixa)).minus(baixa.descontoNoPrincipal)
		}
		return resultado(principal, new D(0), new D(0))
	}

	// 2. A multa nasce uma vez, sobre o principal que passou da carência.
	let multa = formaGeraEncargo
		? principal.times(new D(titulo.porcentagemDeMulta || 0).div(100)).toDecimalPlaces(2, Decimal.ROUND_HALF_EVEN)
		: new D(0)
	let juros = new D(0)

	const taxaDiaria = new D(titulo.porcentagemDeJuros || 0).div(100)
	const teto = paraTexto(somarDias(paraData(titulo.dataDeVencimento), TETO_DE_DIAS_DE_ATRASO))
	const fimDoCalculo = menor(dataDeReferencia, teto)
	let inicioDoPeriodo = titulo.dataDeVencimento

	const jurosDoPeriodo = (inicio: string, fim: string): Decimal => {
		const dias = Math.max(0, diasEntre(inicio, fim))
		if (dias === 0) return new D(0)

		let base = D.max(principal, 0)
		if (calcularJurosComMulta) base = base.plus(D.max(multa, 0))

		if (!composto) return base.times(taxaDiaria).times(dias)
		base = base.plus(D.max(juros, 0))
		return base.times(new D(1).plus(taxaDiaria).pow(dias)).minus(base)
	}

	// 3 e 4. Período a período, cada baixa fecha um.
	for (; proxima < emOrdem.length; proxima++) {
		const baixa = emOrdem[proxima]
		const fimDoPeriodo = menor(baixa.data, fimDoCalculo)
		juros = juros.plus(jurosDoPeriodo(inicioDoPeriodo, fimDoPeriodo))
		inicioDoPeriodo = maior(inicioDoPeriodo, fimDoPeriodo)

		multa = multa.minus(baixa.descontoNaMulta)
		juros = juros.minus(baixa.descontoNosJuros)
		principal = principal.minus(baixa.descontoNoPrincipal)

		let dinheiro = recebido(baixa)
		if (dinheiro.lt(0)) {
			// Estorno: devolve a cada parte o que a baixa de origem tinha abatido dela.
			multa = multa.minus(baixa.multa)
			juros = juros.minus(baixa.juros)
			principal = principal.minus(baixa.principal)
			continue
		}

		const naMulta = D.min(dinheiro, D.max(multa, 0))
		multa = multa.minus(naMulta)
		dinheiro = dinheiro.minus(naMulta)

		const nosJuros = D.min(dinheiro, D.max(juros, 0))
		juros = juros.minus(nosJuros)
		dinheiro = dinheiro.minus(nosJuros)

		principal = principal.minus(dinheiro)
	}

	juros = juros.plus(jurosDoPeriodo(inicioDoPeriodo, fimDoCalculo))

	return resultado(principal, juros, multa)
}

function recebido(baixa: BaixaParaEncargos): Decimal {
	return new D(baixa.principal).plus(baixa.juros).plus(baixa.multa)
}

function resultado(principal: Decimal, juros: Decimal, multa: Decimal): EncargosApurados {
	const principalDevido = D.max(principal, 0)
	const jurosDevidos = D.max(juros, 0).toDecimalPlaces(2, Decimal.ROUND_HALF_EVEN)
	const multaDevida = D.max(multa, 0).toDecimalPlaces(2, Decimal.ROUND_HALF_EVEN)

	return {
		principal: principalDevido.toDecimalPlaces(2, Decimal.ROUND_HALF_EVEN).toNumber(),
		juros: jurosDevidos.toNumber(),
		multa: multaDevida.toNumber(),
		valorAtualizado: principalDevido.plus(jurosDevidos).plus(multaDevida)
			.toDecimalPlaces(2, Decimal.ROUND_HALF_EVEN).toNumber(),
	}
}

function paraData(texto: string): Date {
	const [ano, mes, dia] = texto.substring(0, 10).split('-').map(Number)
	return new Date(Date.UTC(ano, mes - 1, dia))
}

function paraTexto(data: Date): string {
	return data.toISOString().substring(0, 10)
}

function somarDias(data: Date, dias: number): Date {
	return new Date(data.getTime() + dias * UM_DIA_EM_MS)
}

function diasEntre(inicio: string, fim: string): number {
	return Math.round((paraData(fim).getTime() - paraData(inicio).getTime()) / UM_DIA_EM_MS)
}

function menor(a: string, b: string): string {
	return a < b ? a : b
}

function maior(a: string, b: string): string {
	return a > b ? a : b
}
